import { PageConfig } from '../types/config/PageConfig';
import { getPageMetaData } from '../content/pageInfo';

export function getPageConfig(path: string): PageConfig | null  {

  const pageMetaDataInfo = getPageMetaData(path);

  // Handle settings sub-routes specially since they won't have exact metadata
  if (path.startsWith('/settings')) {
    const settingsPaths: { [key: string]: string } = {
      '/settings': 'General',
      '/settings/general': 'General',
      '/settings/auth-providers': 'Authentication',
      '/settings/feature-flags': 'Feature Flags',
    };
    
    const pageTitle = settingsPaths[path] || 'Settings';
    
    if (path === '/settings') {
      return {
        breadcrumbs: [{ label: 'Settings' }],
        title: pageMetaDataInfo?.title || 'Arla Foods AMBA Settings',
        description: pageMetaDataInfo?.description || 'Manage your profile and preferences',
      };
    }
    
    return {
      breadcrumbs: [
        { label: 'Settings', path: '/settings' },
        { label: pageTitle }
      ],
      title: `${pageTitle} Settings`,
      description: `Manage your ${pageTitle.toLowerCase()} settings`,
    };
  }

  // Handle product team detail pages specially since they won't have exact metadata.
  // Routes here are /product-teams/{id}[/tooling-groups/...] — the URL only carries the
  // team's id, not its display name, and this function has no way to fetch the real one
  // (it's called synchronously from the URL alone). This is only ever
  // shown for the brief moment before the page itself has fetched the team and announced its
  // real `name` via pageHeaderOverrideStore (see utils/pageHeaderOverride.ts and
  // GlobalBreadcrumb.tsx, which checks that override before falling back to this function) —
  // so render a loading skeleton for the not-yet-known team name instead of the raw id.
  if (path.startsWith('/product-teams/')) {
    const rest = path.slice('/product-teams/'.length);
    const segments = rest.split('/');
    const trailing = segments.slice(1);
    // SonarQube group pages: tooling-groups/sonarqube/{serviceGroupId}/...
    const sonarQube = trailing.length >= 3 && trailing[0] === 'tooling-groups' && trailing[1] === 'sonarqube' ? trailing.slice(3) : undefined;
    const teamCrumb = { label: '', loading: true };

    // Create new tooling group page: tooling-groups/{sonarqube|devbox}/create
    if (trailing.length === 3 && trailing[0] === 'tooling-groups' && trailing[2] === 'create') {
      return {
        breadcrumbs: [
          { label: 'Product Teams', path: '/product-teams' },
          teamCrumb,
          { label: 'Create new group' },
        ],
        title: 'Create new group',
      };
    }

    // Create SonarQube project page: tooling-groups/sonarqube/{serviceGroupId}/projects/create
    if (sonarQube?.join('/') === 'projects/create') {
      return {
        breadcrumbs: [
          { label: 'Product Teams', path: '/product-teams' },
          teamCrumb,
          { label: '', loading: true },
          { label: 'Create SonarQube Project' },
        ],
        title: 'Create SonarQube Project',
      };
    }

    // Update SonarQube project page: tooling-groups/sonarqube/{serviceGroupId}/projects/{projectKey}/edit
    if (sonarQube?.length === 3 && sonarQube[0] === 'projects' && sonarQube[2] === 'edit') {
      return {
        breadcrumbs: [
          { label: 'Product Teams', path: '/product-teams' },
          teamCrumb,
          { label: '', loading: true },
          { label: 'Update SonarQube Project' },
        ],
        title: 'Update SonarQube Project',
      };
    }

    // Update tooling group page: tooling-groups/sonarqube/{serviceGroupId}/edit
    if (sonarQube?.join('/') === 'edit') {
      return {
        breadcrumbs: [
          { label: 'Product Teams', path: '/product-teams' },
          teamCrumb,
          { label: '', loading: true },
          { label: 'Update tooling group' },
        ],
        title: 'Update tooling group',
      };
    }

    // Tooling group page: tooling-groups/sonarqube/{serviceGroupId}
    if (sonarQube?.length === 0) {
      return {
        breadcrumbs: [
          { label: 'Product Teams', path: '/product-teams' },
          teamCrumb,
          { label: '', loading: true },
        ],
        title: '',
        titleLoading: true,
      };
    }

    return {
      breadcrumbs: [
        { label: 'Product Teams', path: '/product-teams' },
        teamCrumb,
      ],
      title: '',
      titleLoading: true,
    };
  }

  if (!pageMetaDataInfo) {
      // Entity docs pages are handled by TechDocsCustomLayout
      if (path.startsWith('/docs/')) {
        return null;
      }

      return null;
  }
    
    // Catalog index page
    if (path === '/catalog') {
      return {
        breadcrumbs: [{ label: 'Catalog' }],
        title: pageMetaDataInfo.title,
        description: pageMetaDataInfo.description,
      };
    }
    
    // Docs index page
    if (path === '/docs') {
      return {
        breadcrumbs: [{ label: 'Documentation' }],
        title: pageMetaDataInfo.title,
        description: pageMetaDataInfo.description,
      };
    }
    
    // API Docs
    if (path === '/api-docs') {
      return {
        breadcrumbs: [{ label: 'APIs' }],
        title: pageMetaDataInfo.title,
        description: pageMetaDataInfo.description,
      };
    }
    
    // Search
    if (path === '/search') {
      return {
        breadcrumbs: [{ label: 'Search' }],
        title: pageMetaDataInfo.title,
        description: pageMetaDataInfo.description,
      };
    }

    // Product Teams
    if (path === '/product-teams') {
      return {
        breadcrumbs: [{ label: 'Product Teams' }],
        title: pageMetaDataInfo.title,
        description: pageMetaDataInfo.description,
      };
    }
    
    // Create/Templates
    if (path === '/create') {
      return {
        breadcrumbs: [{ label: 'Create' }],
        title: pageMetaDataInfo.title,
        description: pageMetaDataInfo.description,
      };
    }

    if (path === '/catalog-import') {
        return {
          breadcrumbs: [{ label: 'Catalog Import' }],
          title: pageMetaDataInfo.title,
          description: pageMetaDataInfo.description,
        };
    }
    
    // Entity pages are handled by EntityPageLayout
    if (path.startsWith('/catalog/')) {
      return null; // Don't render - EntityPageLayout handles it
    }

    if (path.startsWith('/docs/')) {
      return null; // Don't render - EntityPageLayout handles it
    }
    
    // Default: no header
    return null;
  };